// Acceptance-deadline rules shared by the offer wizard and both counter-offer
// forms (CounterOfferModal, NegotiationWorkspace's CounterOfferForm).
// Pure helpers — no React imports.
//
// A deadline is 11:59 PM on the chosen date in the PROPERTY's timezone (legal
// review 2026-09-29, M2): both parties, wherever they are, and the purchase
// agreement read one instant. The client sends only the date (`expires_on`);
// the backend computes the instant. `timeZone` below is the property's IANA
// zone (`house.timezone`, `offer.property_timezone`); when it is unknown the
// viewer's own zone is used, which only affects display.
import { parseDateOnly, toDateOnly } from './utils';

// Mirrors OFFER_DEFAULT_VALID_DAYS / OFFER_MAX_VALID_DAYS in backend schemas.py.
export const OFFER_DEFAULT_VALID_DAYS = 7;
export const OFFER_MAX_VALID_DAYS = 30;

/** Today's date (`YYYY-MM-DD`) in `timeZone`. */
export const todayInZone = (timeZone) => {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    return toDateOnly(new Date());
  }
};

/** `YYYY-MM-DD` for `days` from today in `timeZone`. */
export const dateInDaysInZone = (days, timeZone) => {
  const d = parseDateOnly(todayInZone(timeZone));
  d.setDate(d.getDate() + days);
  return toDateOnly(d);
};

/** Short zone name for an instant, e.g. "CDT". Empty when unavailable. */
export const zoneAbbreviation = (timeZone, at = new Date()) => {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
  } catch {
    return '';
  }
};

/**
 * "11:59 PM CDT" for a deadline falling on `dateOnly` in `timeZone` — the
 * abbreviation follows DST on that date, not today.
 */
export const deadlineTimeLabel = (dateOnly, timeZone) => {
  const day = parseDateOnly(dateOnly);
  // Noon local avoids any DST-transition edge when picking the abbreviation.
  const zone = day
    ? zoneAbbreviation(timeZone, new Date(day.getFullYear(), day.getMonth(), day.getDate(), 12))
    : zoneAbbreviation(timeZone);
  return zone ? `11:59 PM ${zone}` : '11:59 PM';
};

/**
 * Format a stored deadline instant in the property's zone,
 * e.g. "Oct 6, 2026, 11:59 PM CDT".
 */
export const formatDeadline = (expiresAt, timeZone, fallback = '—') => {
  if (!expiresAt) return fallback;
  const at = new Date(expiresAt);
  if (isNaN(at.getTime())) return fallback;
  try {
    const text = at.toLocaleString('en-US', { timeZone, dateStyle: 'medium', timeStyle: 'short' });
    const zone = zoneAbbreviation(timeZone, at);
    return zone ? `${text} ${zone}` : text;
  } catch {
    return at.toLocaleString();
  }
};

/** True once a stored deadline has passed. */
export const isDeadlinePast = (expiresAt) => {
  if (!expiresAt) return false;
  const at = new Date(expiresAt);
  return !isNaN(at.getTime()) && at.getTime() <= Date.now();
};

/**
 * Why the chosen "valid until" date cannot be used, or null when it can.
 * Date-only strings compare correctly as strings.
 *
 * @param {string} expiresOn `YYYY-MM-DD`
 * @param {string} [closingDate] `YYYY-MM-DD`
 * @param {string} [timeZone] property IANA zone
 * @returns {string|null}
 */
export const getExpirationError = (expiresOn, closingDate, timeZone) => {
  if (!expiresOn) return 'Choose how long your offer is valid';
  if (expiresOn < todayInZone(timeZone)) return 'Offer expiration cannot be in the past';
  if (expiresOn > dateInDaysInZone(OFFER_MAX_VALID_DAYS, timeZone)) {
    return `Offer can be valid for at most ${OFFER_MAX_VALID_DAYS} days`;
  }
  if (closingDate && expiresOn >= closingDate) {
    return 'Offer must expire before the closing date';
  }
  return null;
};

/**
 * `min`/`max` for a "valid until" date input: from today to the earlier of
 * the 30-day cap and the day before closing.
 *
 * @param {string} [closingDate] `YYYY-MM-DD`
 * @param {string} [timeZone] property IANA zone
 * @returns {{min: string, max: string}}
 */
export const getExpirationBounds = (closingDate, timeZone) => {
  const min = todayInZone(timeZone);
  const cap = dateInDaysInZone(OFFER_MAX_VALID_DAYS, timeZone);
  const closing = parseDateOnly(closingDate);
  if (!closing || closingDate > cap) return { min, max: cap };
  closing.setDate(closing.getDate() - 1);
  return { min, max: toDateOnly(closing) };
};

/**
 * Default "valid until" date: OFFER_DEFAULT_VALID_DAYS out, pulled in to the
 * day before closing when closing is sooner.
 *
 * @param {string} [closingDate] `YYYY-MM-DD`
 * @param {string} [timeZone] property IANA zone
 * @returns {string} `YYYY-MM-DD`
 */
export const getDefaultExpiresOn = (closingDate, timeZone) => {
  const preferred = dateInDaysInZone(OFFER_DEFAULT_VALID_DAYS, timeZone);
  const { max } = getExpirationBounds(closingDate, timeZone);
  return preferred > max ? max : preferred;
};

/** True when the chosen date gives the other party less than a day. */
export const isShortDeadline = (expiresOn, timeZone) =>
  !!expiresOn && expiresOn === todayInZone(timeZone);

/**
 * How close a still-live offer is to lapsing, for an escalating cue on the card
 * so it isn't missed: `urgent` within a day (counted in hours), `soon` within
 * three days (counted in days), otherwise `none`. A deadline already past is the
 * card's "Expired on" line and returns `none` here.
 *
 * `now` is injectable so the cue is testable without mocking the clock.
 *
 * @param {string} expiresAt ISO instant (`offer.expires_at`)
 * @param {number} [now] epoch ms to measure from
 * @returns {{ level: ('urgent'|'soon'|'none'), relative: (string|null) }}
 */
export const getExpiryUrgency = (expiresAt, now = Date.now()) => {
  if (!expiresAt) return { level: 'none', relative: null };
  const at = new Date(expiresAt).getTime();
  if (isNaN(at)) return { level: 'none', relative: null };
  const ms = at - now;
  if (ms <= 0) return { level: 'none', relative: null };
  const hours = ms / 3600000;
  if (hours < 1) return { level: 'urgent', relative: 'in under an hour' };
  if (hours < 24) {
    const h = Math.round(hours);
    return { level: 'urgent', relative: `in ${h} hour${h === 1 ? '' : 's'}` };
  }
  const days = Math.floor(hours / 24);
  if (days <= 2) return { level: 'soon', relative: `in ${days} day${days === 1 ? '' : 's'}` };
  return { level: 'none', relative: null };
};
